import { ApplicationPartGet, ApplicationPartPatch, ApplicationPartController, Controllers } from "@infoland/iprova-api";
import VueI18n from "vue-i18n";
import { ActionTree, GetterTree, MutationTree } from "vuex";
import { RootState } from "..";
import { SnackBarTimeout } from "@/enums";

export interface AppMessage {
    /** the message text */
    text: string;
    /** the auto hide delay in seconds */
    timeout?: SnackBarTimeout;
    /** Checks is the message is for error or not */
    isError: boolean;
    languageLookup: boolean;
    lookupData: VueI18n.Values;
}

export interface Category {
    categoryId: number;
    name: string;
    order: number;
}
// state type
export interface SiteState {
    title: string;
    message: AppMessage;
    /* eslint-disable */
    application_parts: ApplicationPartGet[];
    dialogsDispalyed: number;
    canGoBack: boolean;
    categories: Category[];
}

export function initialize(): SiteState {
    return {
        title: "",
        application_parts: null,
        message: { text: null, timeout: null, isError: false, languageLookup: false, lookupData: null },
        dialogsDispalyed: 0,
        canGoBack: false,
        categories: null
    };
}

// initial state
const state: SiteState = initialize();

// getters for the site store
const getters: GetterTree<SiteState, RootState> = {
    /**
     * The title of the site
     * @param state The state to read from
     */
    title(state: SiteState): string {
        return state.title;
    },
    /**
     * Gets the snackBar message
     * @param state
     */
    snackBarMessage(state: SiteState): AppMessage {
        return state.message;
    },

    application_parts(state: SiteState): ApplicationPartGet[] {
        return state.application_parts;
    },

    isDialogDisplayed(state: SiteState): boolean {
        return state.dialogsDispalyed > 0;
    },

    canGoBack(state: SiteState): boolean {
        return state.canGoBack;
    }
};

// mutations for the site store
const mutations: MutationTree<SiteState> = {
    /**
     * Changes the site title
     * @param state The state to write to
     * @param title The new value for the site title
     */
    title(state: SiteState, title: string): void {
        // make title the empty string if null or not defined
        if (title === null || title === undefined) {
            title = "";
        }

        state.title = title;
    },
    /**
     * Sets message
     * @param state
     * @param message a message
     */
    _setSnackBarMessage(state: SiteState, message: AppMessage): void {
        // previous message timeout?
        if (state.message.timeout) {
            // cancel previous message timeout
            clearTimeout(state.message.timeout);
        }

        // set message to blank
        state.message.text = null;

        // treat blank string as no message
        if (message == null || /^\s*$/.test(message.text)) {
            return;
        }

        // set new message text
        state.message.text = message.text;
        state.message.isError = message.isError;
        state.message.languageLookup = message.languageLookup;
        state.message.lookupData = message.lookupData;

        // set auto clear
        if (!message.timeout) message.timeout = SnackBarTimeout.medium; // default timeout is 3 seconds

        // set the timeout value to the ID of the timeout
        state.message.timeout = window.setTimeout(() => {
            // remove timeout id and message
            state.message.timeout = null;
            state.message.text = null;
            state.message.lookupData = null;
            state.message.isError = false;
            state.message.languageLookup = false;
        }, message.timeout * 1000); // message.timeout is in seconds
    },

    _application_parts(state: SiteState, application_parts: ApplicationPartGet[]): void {
        state.application_parts = application_parts;
    },

    _application_part(state: SiteState, application_part: ApplicationPartPatch): void {
        let part = state.application_parts.find(p => p.application_part_id == application_part.application_part_id);
        part.favorite = application_part.favorite;
    },
    _setIsDialogDisplayed(state: SiteState, isDialogDisplayed: boolean): void {
        if (isDialogDisplayed) {
            state.dialogsDispalyed += 1;
        } else {
            state.dialogsDispalyed -= 1;
        }
    },
    _categories(state: SiteState, categories: Category[]): void {
        state.categories = categories;
    },
    canGoBack(state: SiteState, canGoBack: boolean): void {
        state.canGoBack = canGoBack;
    }
};
const actions: ActionTree<SiteState, RootState> = {
    setSnackBarMessage({ commit }, message: AppMessage): void {
        commit("_setSnackBarMessage", message);
    },
    async loadApplicationPart({ commit }): Promise<ApplicationPartGet[]> {
        const application_parts: ApplicationPartGet[] = await Controllers.ApplicationPartController.getApplicationParts();

        //sort the application parts on display name
        if (application_parts) {
            application_parts.sort((a, b) => a.display_name.localeCompare(b.display_name));
        }

        commit("_application_parts", application_parts);
        return application_parts;
    },
    async updateApplicationPartFavorite({ commit }, application_part: ApplicationPartPatch): Promise<void> {
        //we commit the change first to the state for UX-purposes.
        commit("_application_part", application_part);
        await Controllers.ApplicationPartController.saveApplicationPart(application_part.application_part_id, application_part);
    },
    async getCategories({ commit, state }): Promise<Category[]> {
        let orderedCategories: Category[];
        if (state.categories === null) {
            const categoryInfo = await Controllers.GenericController.getAsync<{ category_id: number; name: string; order: number }[]>("categories");
            orderedCategories = categoryInfo.map(c => {
                return {
                    categoryId: c.category_id,
                    name: c.name,
                    order: c.order
                } as Category;
            });
            commit("_categories", orderedCategories);
        } else {
            orderedCategories = state.categories;
        }

        return orderedCategories;
    },
    setIsDialogDisplayed({ commit }, isDialogDisplayed: boolean): void {
        commit("_setIsDialogDisplayed", isDialogDisplayed);
    }
};

// export the namespace
export const namespace = "site";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
